import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  X, History, Loader2, RotateCcw, CheckCircle2, FileText, Eye,
  Clock, User, Plus, AlertTriangle,
} from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { opts, fmt } from "./constants";

/**
 * PDF REV3 #5 + #6 — Modal de Histórico de Revisiones.
 *
 * Lista todas las revisiones del presupuesto:
 *   REV0, REV01, REV02, ... + (actual)
 *
 * Permite:
 *   - Ver el detalle de una revisión vieja (read-only)
 *   - Restaurar una revisión vieja (la promueve a la actual, archiva la actual)
 *   - Crear una nueva revisión a partir del estado actual (opcionalmente con un comentario)
 */
export function RevisionsModal({ open, budgetId, currentBudget, onClose, onRestored, onRevisionCreated }) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createComment, setCreateComment] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);

  const fetchRevisions = useCallback(async () => {
    if (!budgetId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/budgets/${budgetId}/revisions`, opts);
      setRevisions(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar revisiones");
    } finally {
      setLoading(false);
    }
  }, [budgetId]);

  useEffect(() => {
    if (open) {
      fetchRevisions();
      setViewing(null);
      setConfirmRestore(null);
      setShowCreateForm(false);
      setCreateComment("");
    }
  }, [open, fetchRevisions]);

  if (!open) return null;

  const handleRestore = async (rev) => {
    setLoading(true);
    try {
      await axios.post(`${API}/budgets/${budgetId}/revisions/${rev._rev_id}/restore`, {}, opts);
      toast.success(`Revisión ${rev.label} restaurada como versión actual`);
      setConfirmRestore(null);
      if (onRestored) onRestored();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al restaurar revisión");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/budgets/${budgetId}/create-revision`, { comment: createComment }, opts);
      toast.success("Nueva revisión creada — la versión anterior quedó archivada");
      setShowCreateForm(false);
      setCreateComment("");
      await fetchRevisions();
      if (onRevisionCreated) onRevisionCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear revisión");
    } finally {
      setCreating(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[75] flex items-center justify-center p-4" data-testid="revisions-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-[#EBF3FB] rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#2E6DA4] rounded-xl flex items-center justify-center">
              <History size={18} className="text-white" />
            </div>
            <div>
              <h3 className="text-base font-bold text-[#1A4B7A]">Histórico de Revisiones</h3>
              <p className="text-xs text-[#2E6DA4]">
                {currentBudget?.nombre || "Presupuesto"} · {revisions.length} versi{revisions.length === 1 ? "ón" : "ones"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!showCreateForm && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-3 py-2 rounded-lg text-xs font-bold transition-colors shadow-sm"
                data-testid="create-revision-btn"
              >
                <Plus size={13} /> Nueva Revisión
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/60 transition-colors">
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Formulario inline para crear revisión */}
        {showCreateForm && (
          <div className="px-6 py-4 border-b border-gray-100 bg-blue-50/40">
            <div className="flex items-start gap-2 mb-2">
              <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                Se creará un <strong>snapshot</strong> de la versión actual (archivada como histórico) y se incrementará la revisión.
                La versión anterior quedará accesible para consulta o restauración.
              </p>
            </div>
            <label className="block text-xs font-bold text-gray-700 mb-1.5">
              Motivo / Comentario <span className="text-gray-400 font-normal">(opcional)</span>
            </label>
            <textarea
              value={createComment}
              onChange={(e) => setCreateComment(e.target.value)}
              rows={2}
              placeholder="Ej: Ajuste de cantidades por pedido del cliente — agosto 2026"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-[#2E6DA4]/30 resize-none"
              data-testid="create-revision-comment-input"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => { setShowCreateForm(false); setCreateComment(""); }}
                className="px-4 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex items-center gap-1.5 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-1.5 rounded-lg text-xs font-bold disabled:opacity-60"
                data-testid="confirm-create-revision-btn"
              >
                {creating ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                Crear Revisión
              </button>
            </div>
          </div>
        )}

        {/* Lista */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : revisions.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <History size={32} className="mx-auto mb-2 opacity-30" />
              <p className="font-medium">Sin revisiones registradas</p>
              <p className="text-xs mt-1">Aún no se creó ninguna revisión histórica.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {revisions.map((rev) => (
                <div
                  key={rev._rev_id}
                  className={`relative rounded-xl border p-4 transition-all ${
                    rev.is_current
                      ? "border-emerald-300 bg-emerald-50/60 ring-2 ring-emerald-200"
                      : "border-gray-200 hover:border-[#2E6DA4]/40 hover:bg-blue-50/30"
                  }`}
                  data-testid={`revision-card-${rev._rev_id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                            rev.is_current
                              ? "bg-emerald-600 text-white"
                              : "bg-[#2E6DA4] text-white"
                          }`}
                        >
                          {rev.label}
                        </span>
                        {rev.is_current && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                            <CheckCircle2 size={10} /> ACTUAL
                          </span>
                        )}
                        {rev.status_at_snapshot && (
                          <span className="text-[10px] font-semibold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                            {rev.status_at_snapshot.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-900 font-semibold">
                        {rev.comment || (rev.is_current ? "Versión editable" : "Snapshot")}
                      </p>
                      <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-1 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock size={11} /> {rev.snapshot_at?.slice(0, 19).replace("T", " ") || "—"}
                        </span>
                        {rev.snapshot_by_name && (
                          <span className="flex items-center gap-1">
                            <User size={11} /> {rev.snapshot_by_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <FileText size={11} /> {rev.items_count || 0} ítem{rev.items_count !== 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-base font-bold text-[#1A4B7A] tabular-nums">{fmt(rev.total || 0)}</p>
                      <p className="text-[10px] text-gray-400">Total IVA inc.</p>
                    </div>
                  </div>

                  {!rev.is_current && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => setViewing(rev)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#2E6DA4]/10 hover:bg-[#2E6DA4]/20 text-[#2E6DA4] text-xs font-semibold transition-colors"
                        data-testid={`view-revision-${rev._rev_id}`}
                      >
                        <Eye size={12} /> Ver detalle
                      </button>
                      <button
                        onClick={() => setConfirmRestore(rev)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 text-xs font-semibold transition-colors"
                        data-testid={`restore-revision-${rev._rev_id}`}
                      >
                        <RotateCcw size={12} /> Restaurar como actual
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex items-center justify-between">
          <p className="text-[10px] text-gray-500">
            Las revisiones se preservan al crear una nueva versión. Podés restaurar cualquier versión vieja en cualquier momento.
          </p>
          <button onClick={onClose} className="text-xs font-medium text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded-lg">
            Cerrar
          </button>
        </div>
      </div>

      {/* Sub-modal: Ver detalle de una revisión */}
      {viewing && <RevisionDetail budgetId={budgetId} rev={viewing} onClose={() => setViewing(null)} />}

      {/* Sub-modal: Confirmar restauración */}
      {confirmRestore && (
        <ConfirmRestore
          rev={confirmRestore}
          onClose={() => setConfirmRestore(null)}
          onConfirm={() => handleRestore(confirmRestore)}
          loading={loading}
        />
      )}
    </div>,
    document.body
  );
}

function RevisionDetail({ budgetId, rev, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: full } = await axios.get(`${API}/budgets/${budgetId}/revisions/${rev._rev_id}`, opts);
        if (!cancelled) setData(full);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al cargar revisión");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [budgetId, rev._rev_id]);

  const items = data?.items || [];

  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4" data-testid="revision-detail-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-[#1A4B7A]">{rev.label} — Detalle (read-only)</h3>
            <p className="text-xs text-gray-500">{rev.comment || "Snapshot histórico"}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-6 space-y-2">
              {[1, 2, 3].map((i) => <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />)}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-[#EBF3FB] sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-bold text-[#1A4B7A]">N°</th>
                  <th className="text-left px-3 py-2 font-bold text-[#1A4B7A]">Descripción</th>
                  <th className="text-center px-3 py-2 font-bold text-[#1A4B7A]">Un.</th>
                  <th className="text-right px-3 py-2 font-bold text-[#1A4B7A]">Cant.</th>
                  <th className="text-right px-3 py-2 font-bold text-[#1A4B7A]">Precio Unit.</th>
                  <th className="text-right px-3 py-2 font-bold text-[#1A4B7A]">Total Final</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={idx} className={it.is_group ? "bg-[#D9EAF8] font-bold text-[#1A4B7A]" : "border-b border-gray-50"}>
                    <td className="px-3 py-1.5">{it.numero || ""}</td>
                    <td className="px-3 py-1.5">{it.descripcion || ""}</td>
                    <td className="px-3 py-1.5 text-center">{it.is_group ? "" : it.unidad}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{it.is_group ? "" : it.cantidad}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{it.is_group ? "" : fmt(Math.round(it.precio_unit || 0))}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-bold">{it.is_group ? "" : fmt(Math.round(it.total_final || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-4 text-xs">
          <span className="text-gray-500">Subtotal Neto: <strong className="text-[#1A4B7A]">{fmt(data?.subtotal_neto || 0)}</strong></span>
          <span className="text-gray-500">IVA: <strong className="text-[#1A4B7A]">{fmt(data?.iva_total || 0)}</strong></span>
          <span className="text-gray-500">Total: <strong className="text-[#1A4B7A] text-sm">{fmt(data?.total || 0)}</strong></span>
        </div>
      </div>
    </div>,
    document.body
  );
}

function ConfirmRestore({ rev, onClose, onConfirm, loading }) {
  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="confirm-restore-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl p-7 max-w-md w-full text-center">
        <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <RotateCcw size={22} className="text-amber-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">¿Restaurar revisión {rev.label}?</h3>
        <p className="text-sm text-gray-500 mb-4">
          La versión actual se archivará automáticamente como nueva entrada en el histórico (no se pierde).
          Los datos de <strong>{rev.label}</strong> pasarán a ser la versión editable.
        </p>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60"
            data-testid="confirm-restore-btn"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
            Sí, restaurar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
